<template>
  <span class="ts-count d-inline-block">{{ display }}</span>
</template>

<script lang="ts">
import {
  Component, Prop, Vue, Watch,
} from 'vue-property-decorator';
import { formatCount } from '@/helpers/formatCount';

const DURATION = 1500;

// Counts up once when it scrolls into view. Without IntersectionObserver or with
// reduced motion the final value is shown right away.
@Component
export default class TsAnimatedCount extends Vue {
  @Prop({ required: true }) readonly count!: number;

  current = 0;

  observer: IntersectionObserver | null = null;

  frame = 0;

  animated = false;

  get display() {
    return formatCount(this.animated ? this.current : this.count);
  }

  mounted() {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion || !('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver(([entry]) => {
      if (!entry?.isIntersecting || this.animated || !this.count) return;
      this.observer?.disconnect();
      this.animate();
    }, { threshold: 0.3 });
    this.observer.observe(this.$el);
  }

  beforeDestroy() {
    this.observer?.disconnect();
    window.cancelAnimationFrame(this.frame);
  }

  // The counters arrive from the API after mount: observe again until the first real value is animated
  @Watch('count')
  onCountChange() {
    if (!this.animated && this.observer) this.observer.observe(this.$el);
  }

  animate() {
    this.animated = true;
    let start: number | undefined;
    const step = (timestamp: number) => {
      if (start === undefined) start = timestamp;
      const progress = Math.min((timestamp - start) / DURATION, 1);
      const eased = 1 - (1 - progress) ** 3;
      this.current = Math.round(this.count * eased);
      if (progress < 1) this.frame = window.requestAnimationFrame(step);
    };
    this.frame = window.requestAnimationFrame(step);
  }
}
</script>
